/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Industry Categories - Fintech, HealthTech, B2B SaaS
 */

import { 
  CategoryConfig, 
  IndustryCategory, 
  IndustryScenario, 
  IndustryProduct, 
  IndustryMission,
  RegulatoryFramework,
  BuildingType 
} from '../types';

// ============================================
// CATEGORY CONFIGURATIONS
// ============================================

export const INDUSTRY_CATEGORIES: CategoryConfig[] = [
  // Existing Categories
  {
    id: 'fintech',
    name: 'FinTech',
    tagline: 'Build the Future of Finance',
    icon: 'CreditCard',
    emoji: '💳',
    colors: {
      primary: '#10B981',     // Emerald-500
      secondary: '#059669',   // Emerald-600
      accent: '#34D399',      // Emerald-400
      gradient: 'from-emerald-600 to-emerald-400',
      bgGradient: 'from-emerald-900/30 to-emerald-800/20'
    },
    description: 'Navigate the complex world of digital banking, payments, and cryptocurrency. Master AML, KYC, and PCI compliance while building profitable financial products.',
    frameworks: ['PSD2', 'PCI-DSS', 'AML/KYC', 'SOX', 'GLBA', 'State MTL', 'CFPB'],
    difficulty: 'Intermediate',
    features: [
      'Digital Banking Products',
      'Payment Processing',
      'Cryptocurrency Trading',
      'Consumer Lending',
      'Anti-Money Laundering',
      'Fraud Prevention',
      'Regulatory Compliance'
    ]
  },
  {
    id: 'healthtech',
    name: 'HealthTech',
    tagline: 'Secure Healthcare Innovation',
    icon: 'Heart',
    emoji: '🏥',
    colors: {
      primary: '#3B82F6',     // Blue-500
      secondary: '#1D4ED8',   // Blue-700
      accent: '#60A5FA',      // Blue-400
      gradient: 'from-blue-600 to-blue-400',
      bgGradient: 'from-blue-900/30 to-blue-800/20'
    },
    description: 'Protect patient data while innovating in digital health. Navigate HIPAA, FDA regulations, and clinical research requirements.',
    frameworks: ['HIPAA', 'HITECH', 'FDA 21 CFR Part 11', 'GDPR', 'SOC 2', 'GxP'],
    difficulty: 'Advanced',
    features: [
      'EHR Platforms',
      'Telehealth Services',
      'Wearable Health Tech',
      'Clinical Research',
      'Mental Health',
      'Patient Privacy',
      'Medical Device Security'
    ]
  },
  {
    id: 'saas_b2b',
    name: 'B2B Enterprise',
    tagline: 'Enterprise Solutions at Scale',
    icon: 'Cloud',
    emoji: '💼',
    colors: {
      primary: '#8B5CF6',     // Violet-500
      secondary: '#6D28D9',   // Violet-700
      accent: '#A78BFA',      // Violet-400
      gradient: 'from-violet-600 to-violet-400',
      bgGradient: 'from-violet-900/30 to-violet-800/20'
    },
    description: 'Build industry-specific enterprise software with world-class security. Achieve SOC 2, ISO 27001, and industry-specific compliance.',
    frameworks: ['SOC 2 Type II', 'ISO 27001', 'GDPR', 'CCPA', 'FedRAMP', 'CSA STAR', 'LGPD'],
    difficulty: 'Beginner',
    features: [
      'Vertical SaaS',
      'Developer Tools',
      'AI/ML Platforms',
      'Industry Solutions',
      'Data Analytics',
      'API Management',
      'Cloud Infrastructure'
    ]
  },
  // New Categories
  {
    id: 'insurtech',
    name: 'InsurTech',
    tagline: 'Modernize Insurance',
    icon: 'Shield',
    emoji: '🛡️',
    colors: {
      primary: '#6366F1',     // Indigo-500
      secondary: '#4F46E5',   // Indigo-600
      accent: '#818CF8',      // Indigo-400
      gradient: 'from-indigo-600 to-indigo-400',
      bgGradient: 'from-indigo-900/30 to-indigo-800/20'
    },
    description: 'Transform the insurance industry with technology. Handle policy management, claims processing, and risk assessment while maintaining compliance.',
    frameworks: ['SOC 2', 'ISO 27001', 'GDPR', 'HIPAA', 'SOX', 'NYDFS'],
    difficulty: 'Intermediate',
    features: [
      'Policy Management',
      'Claims Processing',
      'Risk Assessment',
      'Telematics',
      'Fraud Detection',
      'Regulatory Compliance',
      'Customer Portals'
    ]
  },
  {
    id: 'edtech',
    name: 'EdTech',
    tagline: 'Education Reimagined',
    icon: 'GraduationCap',
    emoji: '🎓',
    colors: {
      primary: '#EC4899',     // Pink-500
      secondary: '#DB2777',   // Pink-600
      accent: '#F472B6',      // Pink-400
      gradient: 'from-pink-600 to-pink-400',
      bgGradient: 'from-pink-900/30 to-pink-800/20'
    },
    description: 'Revolutionize learning with technology while protecting student data and ensuring accessibility for all learners.',
    frameworks: ['FERPA', 'COPPA', 'GDPR', 'WCAG', 'Section 508', 'ISTE'],
    difficulty: 'Beginner',
    features: [
      'LMS Platforms',
      'Student Data Privacy',
      'Accessibility Tools',
      'Remote Learning',
      'Skill Assessment',
      'Gamification',
      'Parent Portals'
    ]
  },
  {
    id: 'retail_ecom',
    name: 'Retail & E-commerce',
    tagline: 'Digital Storefronts',
    icon: 'ShoppingCart',
    emoji: '🛒',
    colors: {
      primary: '#F59E0B',     // Amber-500
      secondary: '#D97706',   // Amber-600
      accent: '#FBBF24',      // Amber-400
      gradient: 'from-amber-600 to-amber-400',
      bgGradient: 'from-amber-900/30 to-amber-800/20'
    },
    description: 'Build secure and scalable e-commerce platforms that handle transactions, customer data, and inventory with confidence.',
    frameworks: ['PCI-DSS', 'GDPR', 'CCPA', 'PSD2', 'LGPD', 'ISO 27001'],
    difficulty: 'Beginner',
    features: [
      'Online Marketplaces',
      'Payment Processing',
      'Inventory Management',
      'Customer Data Protection',
      'Fraud Prevention',
      'Multi-channel Retail',
      'Supply Chain Security'
    ]
  },
  {
    id: 'sandbox',
    name: 'Sandbox Mode',
    tagline: 'All Components Unlocked',
    icon: 'Boxes',
    emoji: '🎮',
    colors: {
      primary: '#6B7280',     // Gray-500
      secondary: '#4B5563',   // Gray-600
      accent: '#9CA3AF',      // Gray-400
      gradient: 'from-gray-600 to-gray-400',
      bgGradient: 'from-gray-900/30 to-gray-800/20'
    },
    description: 'Free play mode with all buildings, products, and features unlocked. Perfect for experimentation and learning without constraints.',
    frameworks: ['All Frameworks Available'],
    difficulty: 'All Levels',
    features: [
      'All Buildings Unlocked',
      'All Products Available',
      'No Restrictions',
      'Custom Scenarios',
      'Experimentation Mode',
      'Learning Sandbox',
      'All Industries'
    ]
  }
];

// ============================================
// FINTECH SCENARIOS
// ============================================

export const FINTECH_SCENARIOS: IndustryScenario[] = [
  {
    id: 'fin_neobank_launch',
    category: 'fintech',
    title: 'Neobank Launch',
    description: 'Launch your digital bank from scratch. Build KYC and AML infrastructure before regulators notice.',
    timeline: 'Day 1: Fresh Charter',
    difficulty: 'Easy',
    regulatoryFrameworks: ['AML/KYC', 'GLBA'],
    startingState: {
      budget: 1500,
      comp: 60,
      debt: 0,
      products: [],
      grid: [
        [0,0,0,0,0],
        [0,1,0,0,0],
        [0,0,0,0,0],
        [0,0,0,0,0],
        [0,0,0,0,0]
      ]
    },
    objectives: [
      'Build 1 AML Unit',
      'Build 1 Fraud Ops',
      'Launch Digital Checking',
      'Reach $5,000 Budget'
    ],
    tips: [
      'Start with AML Unit - it\'s required for most banking products',
      'Fraud Ops reduces threat and is needed for payment products',
      'Digital Checking is your first revenue stream',
      'Keep compliance above 50% to avoid regulatory action'
    ],
    voicePrompt: 'Welcome to your new digital bank. Regulators are watching - build your compliance infrastructure fast.',
    questions: [
      {
        q: 'What is the primary purpose of KYC?',
        options: ['A: Generate revenue', 'B: Verify customer identity', 'C: Reduce costs'],
        correct: 'B',
        hint: 'Know Your Customer is about identity verification'
      }
    ],
    unlockedTechs: ['tech_basic_sec']
  },
  {
    id: 'fin_pci_crisis',
    category: 'fintech',
    title: 'PCI Audit Failed',
    description: 'Your payment processor failed PCI-DSS audit. Remediate before card networks cut you off.',
    timeline: '2024: Compliance Crisis',
    difficulty: 'Medium',
    regulatoryFrameworks: ['PCI-DSS'],
    startingState: {
      budget: 800,
      comp: 25,
      debt: 1,
      products: ['prod_checking'],
      grid: [
        [0,1,0,0,0],
        [0,0,5,0,0],
        [0,0,0,0,0],
        [0,0,0,0,0],
        [0,0,0,0,0]
      ]
    },
    objectives: [
      'Build Firewall + SIEM',
      'Build 2 Audit Labs',
      'Reach Compliance > 80%',
      'Repay all debt'
    ],
    tips: [
      'Firewall and SIEM are critical for PCI compliance',
      'Audit Labs generate high compliance to recover quickly',
      'Don\'t launch new products until compliance is stable'
    ],
    voicePrompt: 'PCI audit failed. Visa and Mastercard are threatening to terminate. Fix this now.',
    questions: [
      {
        q: 'What does PCI-DSS protect?',
        options: ['A: Patient data', 'B: Cardholder data', 'C: Employee data'],
        correct: 'B',
        hint: 'PCI stands for Payment Card Industry'
      }
    ],
    unlockedTechs: ['tech_basic_sec', 'tech_adv_cyber'],
    historicalOutcome: 'Companies failing PCI audits face fines of $5,000-$100,000 per month until compliant.'
  },
  {
    id: 'fin_crypto_meltdown',
    category: 'fintech',
    title: 'Crypto Exchange Crisis',
    description: 'Your crypto exchange faces a liquidity crisis. Maintain compliance while surviving the bank run.',
    timeline: '2022: Market Crash',
    difficulty: 'Hard',
    regulatoryFrameworks: ['AML/KYC', 'State MTL', 'SEC'],
    startingState: {
      budget: 2000,
      comp: 40,
      debt: 2,
      products: ['prod_crypto'],
      grid: [
        [0,1,0,0,0],
        [0,5,6,0,0],
        [0,2,0,0,0],
        [0,0,0,0,0],
        [0,0,0,0,0]
      ]
    },
    objectives: [
      'Build 2 AML Units + 1 Sanctions',
      'Build Vault (Cold Storage)',
      'Stabilize Crypto (Risk < 5%)',
      'Reach $10,000 Budget'
    ],
    tips: [
      'Vault is essential for crypto - it secures customer assets',
      'Sanctions screening is mandatory for crypto transactions',
      'Focus on compliance before expanding'
    ],
    voicePrompt: 'The market is crashing. Customers are withdrawing. Build robust controls or face shutdown.',
    questions: [
      {
        q: 'Why is Sanctions screening critical for crypto?',
        options: ['A: Speed', 'B: OFAC compliance', 'C: Lower fees'],
        correct: 'B',
        hint: 'Crypto can be used to evade sanctions'
      }
    ],
    unlockedTechs: ['tech_basic_sec', 'tech_compliance', 'tech_adv_cyber'],
    historicalOutcome: 'FTX collapsed in November 2022, losing $8 billion in customer funds due to lack of controls.',
    scriptedEvents: [
      {
        triggerTick: 15,
        event: {
          id: 'bank_run',
          name: 'Customer Withdrawals Surge',
          description: 'Social media panic triggers mass withdrawals. Budget drains rapidly.',
          type: 'negative'
        }
      }
    ]
  },
  {
    id: 'fin_aml_investigation',
    category: 'fintech',
    title: 'FinCEN Investigation',
    description: 'Federal investigators are examining your AML program. Survive the investigation.',
    timeline: '2023: Federal Scrutiny',
    difficulty: 'Expert',
    regulatoryFrameworks: ['AML/KYC', 'BSA'],
    startingState: {
      budget: 1200,
      comp: 15,
      debt: 0,
      products: ['prod_checking', 'prod_payments'],
      grid: [
        [0,1,0,0,0],
        [0,0,2,0,0],
        [0,5,0,0,0],
        [0,0,0,0,0],
        [0,0,0,0,0]
      ]
    },
    objectives: [
      'Build Internal Audit + Legal Privacy',
      'Build 3 AML Units',
      'Never let Compliance hit 0',
      'Survive 60 ticks without shutdown'
    ],
    tips: [
      'Internal Audit shows regulators you\'re serious',
      'Legal department helps respond to subpoenas',
      'Document everything - evidence matters'
    ],
    voicePrompt: 'FinCEN is at the door. Your AML program is under investigation. This is survival mode.',
    questions: [
      {
        q: 'What triggers a FinCEN investigation?',
        options: ['A: High profits', 'B: Suspicious activity patterns', 'C: New products'],
        correct: 'B',
        hint: 'SAR patterns can trigger federal attention'
      }
    ],
    unlockedTechs: ['tech_basic_sec', 'tech_compliance', 'tech_ops'],
    scriptedEvents: [
      {
        triggerTick: 10,
        event: {
          id: 'subpoena',
          name: 'Federal Subpoena',
          description: 'FinCEN demands all transaction records. Compliance pressure increases.',
          type: 'negative'
        }
      },
      {
        triggerTick: 30,
        event: {
          id: 'asset_freeze',
          name: 'Asset Freeze Warning',
          description: 'Regulators threaten to freeze assets if compliance doesn\'t improve.',
          type: 'negative'
        }
      }
    ]
  }
];

// ============================================
// HEALTHTECH SCENARIOS
// ============================================

export const HEALTHTECH_SCENARIOS: IndustryScenario[] = [
  {
    id: 'health_telehealth_startup',
    category: 'healthtech',
    title: 'Telehealth Launch',
    description: 'Launch a HIPAA-compliant telehealth platform during the pandemic surge.',
    timeline: '2020: Pandemic Response',
    difficulty: 'Easy',
    regulatoryFrameworks: ['HIPAA', 'HITECH'],
    startingState: {
      budget: 1200,
      comp: 55,
      debt: 0,
      products: [],
      grid: [
        [0,0,0,0,0],
        [0,1,0,0,0],
        [0,0,0,0,0],
        [0,0,0,0,0],
        [0,0,0,0,0]
      ]
    },
    objectives: [
      'Build Privacy Office',
      'Build Secure Communications',
      'Launch Telehealth Service',
      'Reach Compliance > 70%'
    ],
    tips: [
      'Privacy Office is your HIPAA foundation',
      'Secure communications protect patient consultations',
      'BAAs are required for all vendors handling PHI'
    ],
    voicePrompt: 'Patients need remote care. Build HIPAA-compliant telehealth infrastructure fast.',
    questions: [
      {
        q: 'What does HIPAA primarily protect?',
        options: ['A: Financial data', 'B: Protected Health Information', 'C: Trade secrets'],
        correct: 'B',
        hint: 'HIPAA is about health information privacy'
      }
    ],
    unlockedTechs: ['tech_basic_sec', 'tech_compliance']
  },
  {
    id: 'health_breach_response',
    category: 'healthtech',
    title: 'PHI Data Breach',
    description: 'Patient health records have been exposed. Respond before OCR investigates.',
    timeline: '2024: Breach Discovered',
    difficulty: 'Medium',
    regulatoryFrameworks: ['HIPAA', 'HITECH', 'State Breach Laws'],
    startingState: {
      budget: 1000,
      comp: 30,
      debt: 0,
      products: ['health_ehr'],
      grid: [
        [0,1,0,0,0],
        [0,0,8,0,0],
        [0,0,0,0,0],
        [0,0,0,0,0],
        [0,0,0,0,0]
      ]
    },
    objectives: [
      'Build Incident Response Team',
      'Build SIEM + Firewall',
      'Reduce Threat < 20%',
      'Reach Compliance > 75%'
    ],
    tips: [
      'HIPAA requires breach notification within 60 days',
      'Incident Response Team is critical for containment',
      'Document all response actions for OCR'
    ],
    voicePrompt: 'PHI exposed. 50,000 patient records compromised. OCR will investigate. Contain this now.',
    questions: [
      {
        q: 'HIPAA breach notification deadline is?',
        options: ['A: 30 days', 'B: 60 days', 'C: 90 days'],
        correct: 'B',
        hint: 'Covered entities have 60 days to notify'
      }
    ],
    unlockedTechs: ['tech_basic_sec', 'tech_adv_cyber'],
    historicalOutcome: 'Anthem breach (2015) exposed 78.8 million records, resulting in $115 million settlement.',
    scriptedEvents: [
      {
        triggerTick: 20,
        event: {
          id: 'ocr_notice',
          name: 'OCR Investigation Notice',
          description: 'HHS Office for Civil Rights opens formal investigation.',
          type: 'negative'
        }
      }
    ]
  },
  {
    id: 'health_fda_audit',
    category: 'healthtech',
    title: 'FDA Software Validation',
    description: 'Your medical imaging AI needs FDA clearance. Pass the 510(k) review.',
    timeline: '2025: FDA Submission',
    difficulty: 'Hard',
    regulatoryFrameworks: ['FDA 21 CFR Part 11', 'FDA SaMD Guidance'],
    startingState: {
      budget: 1500,
      comp: 45,
      debt: 1,
      products: [],
      grid: [
        [0,1,0,0,0],
        [0,0,4,0,0],
        [0,0,0,0,0],
        [0,0,0,0,0],
        [0,0,0,0,0]
      ]
    },
    objectives: [
      'Build 2 Regulatory Compliance Units',
      'Build Internal Audit',
      'Reach Compliance > 85%',
      'Launch Medical Imaging AI'
    ],
    tips: [
      'FDA requires extensive documentation and validation',
      'Software as Medical Device (SaMD) has strict requirements',
      'Quality Management System is essential'
    ],
    voicePrompt: 'FDA 510(k) submission pending. Your software validation must be bulletproof.',
    questions: [
      {
        q: 'What is SaMD?',
        options: ['A: Security Module', 'B: Software as Medical Device', 'C: System Administration'],
        correct: 'B',
        hint: 'FDA regulates software that functions as a medical device'
      }
    ],
    unlockedTechs: ['tech_basic_sec', 'tech_compliance', 'tech_ops']
  },
  {
    id: 'health_clinical_trial',
    category: 'healthtech',
    title: 'Clinical Research Platform',
    description: 'Build a multi-site clinical trial data platform with proper governance.',
    timeline: '2023: Phase III Trial',
    difficulty: 'Expert',
    regulatoryFrameworks: ['HIPAA', 'FDA 21 CFR Part 11', 'ICH GCP', 'IRB'],
    startingState: {
      budget: 2000,
      comp: 50,
      debt: 0,
      products: [],
      grid: [
        [0,1,0,0,0],
        [0,0,0,0,0],
        [0,0,0,0,0],
        [0,0,0,0,0],
        [0,0,0,0,0]
      ]
    },
    objectives: [
      'Build Data Governance x2',
      'Build Privacy Office + Audit Lab',
      'Launch Clinical Research Product',
      'Pass External Audit'
    ],
    tips: [
      'Clinical data requires strict access controls',
      'IRB approval is mandatory for human subjects research',
      'Data integrity is paramount - audit trails everywhere'
    ],
    voicePrompt: 'Multi-site clinical trial launching. Data governance must be perfect.',
    questions: [
      {
        q: 'What does IRB stand for?',
        options: ['A: Internal Review Board', 'B: Institutional Review Board', 'C: Information Review Bureau'],
        correct: 'B',
        hint: 'IRBs protect human research subjects'
      }
    ],
    unlockedTechs: ['tech_basic_sec', 'tech_compliance', 'tech_ops', 'tech_adv_cyber']
  }
];

// ============================================
// B2B SAAS SCENARIOS
// ============================================

export const SAAS_SCENARIOS: IndustryScenario[] = [
  {
    id: 'saas_soc2_journey',
    category: 'saas_b2b',
    title: 'SOC 2 Certification',
    description: 'Your startup needs SOC 2 Type II to close enterprise deals. Build the controls.',
    timeline: 'Year 1: Enterprise Ready',
    difficulty: 'Easy',
    regulatoryFrameworks: ['SOC 2 Type II'],
    startingState: {
      budget: 1000,
      comp: 50,
      debt: 0,
      products: [],
      grid: [
        [0,0,0,0,0],
        [0,1,0,0,0],
        [0,0,0,0,0],
        [0,0,0,0,0],
        [0,0,0,0,0]
      ]
    },
    objectives: [
      'Build Compliance Management',
      'Build Monitoring + Audit Lab',
      'Reach Compliance > 80%',
      'Launch CRM Platform'
    ],
    tips: [
      'SOC 2 covers Security, Availability, Processing Integrity, Confidentiality, Privacy',
      'Start with Security controls - they\'re foundational',
      'Evidence collection is key - document everything'
    ],
    voicePrompt: 'Enterprise customers require SOC 2. Build your compliance program from scratch.',
    questions: [
      {
        q: 'SOC 2 Type II differs from Type I by?',
        options: ['A: Cost', 'B: Testing period', 'C: Number of controls'],
        correct: 'B',
        hint: 'Type II tests controls over a period of time'
      }
    ],
    unlockedTechs: ['tech_basic_sec', 'tech_compliance']
  },
  {
    id: 'saas_gdpr_sprint',
    category: 'saas_b2b',
    title: 'GDPR Compliance Sprint',
    description: 'GDPR enforcement begins in 30 days. Make your SaaS compliant for EU customers.',
    timeline: '2018: GDPR Deadline',
    difficulty: 'Medium',
    regulatoryFrameworks: ['GDPR'],
    startingState: {
      budget: 1200,
      comp: 40,
      debt: 0,
      products: ['saas_crm'],
      grid: [
        [0,1,0,0,0],
        [0,0,3,0,0],
        [0,0,0,0,0],
        [0,0,0,0,0],
        [0,0,0,0,0]
      ]
    },
    objectives: [
      'Build GDPR Office',
      'Build Data Protection Officer',
      'Build Privacy Office',
      'Reach Compliance > 75%'
    ],
    tips: [
      'GDPR requires a DPO for certain organizations',
      'Data subject rights must be implemented',
      'Privacy by design is mandatory'
    ],
    voicePrompt: 'GDPR enforcement in 30 days. EU customers need compliant data processing.',
    questions: [
      {
        q: 'GDPR maximum fine is?',
        options: ['A: $1 million', 'B: 4% of global revenue', 'C: $10 million'],
        correct: 'B',
        hint: 'GDPR fines are percentage-based'
      }
    ],
    unlockedTechs: ['tech_basic_sec', 'tech_compliance'],
    historicalOutcome: 'GDPR fines exceeded €1.5 billion in the first three years of enforcement.'
  },
  {
    id: 'saas_supply_chain',
    category: 'saas_b2b',
    title: 'Supply Chain Attack',
    description: 'A vendor compromise has exposed your customers. Contain and recover.',
    timeline: '2024: Vendor Breach',
    difficulty: 'Hard',
    regulatoryFrameworks: ['SOC 2', 'ISO 27001', 'Customer Contracts'],
    startingState: {
      budget: 1500,
      comp: 35,
      debt: 0,
      products: ['saas_crm', 'saas_analytics'],
      grid: [
        [0,1,0,0,0],
        [0,0,14,0,0],
        [0,3,0,0,0],
        [0,0,0,0,0],
        [0,0,0,0,0]
      ]
    },
    objectives: [
      'Build Incident Response + SIEM',
      'Build Vendor Risk Management',
      'Reduce Threat < 25%',
      'Retain 80% of customers'
    ],
    tips: [
      'Vendor risk management prevents future incidents',
      'Incident response is critical for containment',
      'Customer communication is key to retention'
    ],
    voicePrompt: 'Vendor compromised. Your customers\' data may be exposed. Contain this immediately.',
    questions: [
      {
        q: 'Best defense against supply chain attacks?',
        options: ['A: More vendors', 'B: Vendor risk management', 'C: Lower prices'],
        correct: 'B',
        hint: 'TPRM helps assess and monitor vendor security'
      }
    ],
    unlockedTechs: ['tech_basic_sec', 'tech_adv_cyber', 'tech_ops'],
    historicalOutcome: 'SolarWinds attack (2020) compromised 18,000 organizations through a single vendor.',
    scriptedEvents: [
      {
        triggerTick: 10,
        event: {
          id: 'customer_churn',
          name: 'Enterprise Customer Threatens Exit',
          description: 'Your largest customer demands incident report within 24 hours.',
          type: 'negative'
        }
      }
    ]
  },
  {
    id: 'saas_fedramp',
    category: 'saas_b2b',
    title: 'FedRAMP Authorization',
    description: 'Win the government contract by achieving FedRAMP Moderate authorization.',
    timeline: '2025: Government RFP',
    difficulty: 'Expert',
    regulatoryFrameworks: ['FedRAMP', 'NIST 800-53', 'FISMA'],
    startingState: {
      budget: 2000,
      comp: 55,
      debt: 0,
      products: ['saas_crm'],
      grid: [
        [0,1,0,0,0],
        [0,0,3,0,0],
        [0,4,0,0,0],
        [0,0,0,0,0],
        [0,0,0,0,0]
      ]
    },
    objectives: [
      'Build 2 Regulatory Compliance Units',
      'Build SOC + Continuous Monitoring',
      'Reach Compliance > 90%',
      'Launch GovCloud Services'
    ],
    tips: [
      'FedRAMP Moderate requires 325 security controls',
      '3PAO assessment is mandatory',
      'Continuous monitoring is required post-authorization'
    ],
    voicePrompt: 'Government contract worth $50M. FedRAMP authorization required. This is the big leagues.',
    questions: [
      {
        q: 'FedRAMP Moderate requires how many controls?',
        options: ['A: 125', 'B: 325', 'C: 525'],
        correct: 'B',
        hint: 'Moderate impact level has 325 controls'
      }
    ],
    unlockedTechs: ['tech_basic_sec', 'tech_compliance', 'tech_adv_cyber', 'tech_ops', 'tech_zerotrust']
  }
];

// ============================================
// INDUSTRY-SPECIFIC PRODUCTS
// ============================================

export const FINTECH_PRODUCTS: IndustryProduct[] = [
  {
    id: 'fin_checking',
    category: 'fintech',
    name: 'Digital Checking',
    description: 'Basic digital checking account with debit card',
    revenue: 50,
    risk: 2,
    framework: 'AML/KYC',
    requirements: [
      { type: BuildingType.SecurityAML, count: 1 }
    ],
  },
  {
    id: 'fin_savings',
    category: 'fintech',
    name: 'High-Yield Savings',
    description: 'Competitive interest savings account',
    revenue: 40,
    risk: 1,
    framework: 'GLBA',
    requirements: [
      { type: BuildingType.OpsFunding, count: 1 },
      { type: BuildingType.ComplianceMgmt, count: 1 }
    ],
  },
  {
    id: 'fin_payments',
    category: 'fintech',
    name: 'P2P Payments',
    description: 'Instant peer-to-peer money transfers',
    revenue: 80,
    risk: 4,
    framework: 'AML/KYC',
    requirements: [
      { type: BuildingType.SecurityFraud, count: 1 },
      { type: BuildingType.SecurityAML, count: 2 }
    ],
  },
  {
    id: 'fin_lending',
    category: 'fintech',
    name: 'Consumer Lending',
    description: 'Personal loans and credit lines',
    revenue: 120,
    risk: 6,
    framework: 'CFPB',
    requirements: [
      { type: BuildingType.SecurityAML, count: 2 },
      { type: BuildingType.LegalPrivacy, count: 1 },
      { type: BuildingType.ComplianceMgmt, count: 1 }
    ],
  },
  {
    id: 'fin_crypto',
    category: 'fintech',
    name: 'Crypto Trading',
    description: 'Cryptocurrency exchange and custody',
    revenue: 200,
    risk: 10,
    framework: 'AML/KYC',
    requirements: [
      { type: BuildingType.Vault, count: 1 },
      { type: BuildingType.SecurityAML, count: 2 },
      { type: BuildingType.Sanctions, count: 1 },
      { type: BuildingType.Firewall, count: 1 }
    ],
  },
  {
    id: 'fin_cards',
    category: 'fintech',
    name: 'Credit Cards',
    description: 'Consumer and business credit cards',
    revenue: 150,
    risk: 7,
    framework: 'PCI-DSS',
    requirements: [
      { type: BuildingType.SecurityFraud, count: 2 },
      { type: BuildingType.Firewall, count: 1 },
      { type: BuildingType.SIEM, count: 1 }
    ],
  }
];

export const HEALTHTECH_PRODUCTS: IndustryProduct[] = [
  // Core HealthTech Products
  {
    id: 'health_ehr',
    category: 'healthtech',
    name: 'EHR Platform',
    description: 'Comprehensive Electronic Health Records system for healthcare providers',
    revenue: 80,
    risk: 5,
    framework: 'HIPAA',
    requirements: [
      { type: BuildingType.LegalPrivacy, count: 1 },
      { type: BuildingType.Firewall, count: 1 },
      { type: BuildingType.Monitoring, count: 1 }
    ],
  },
  {
    id: 'health_telehealth',
    category: 'healthtech',
    name: 'Telehealth Platform',
    description: 'End-to-end virtual healthcare consultation platform with video, chat, and remote monitoring',
    revenue: 90,
    risk: 6,
    framework: 'HIPAA',
    requirements: [
      { type: BuildingType.LegalPrivacy, count: 1 },
      { type: BuildingType.CloudWAF, count: 1 },
      { type: BuildingType.APIGateway, count: 1 }
    ],
  },
  // New: Wearable Health Devices
  {
    id: 'health_wearable',
    category: 'healthtech',
    name: 'Wearable Health Monitor',
    description: 'Wearable devices that track vital signs and health metrics in real-time',
    revenue: 120,
    risk: 7,
    framework: 'FDA',
    requirements: [
      { type: BuildingType.SecurityTraining, count: 1 },
      { type: BuildingType.CloudWAF, count: 1 },
      { type: BuildingType.DataProtectionOfficer, count: 1 }
    ],
  },
  // New: Clinical Trial Management System
  {
    id: 'health_ctms',
    category: 'healthtech',
    name: 'Clinical Trial Platform',
    description: 'End-to-end clinical trial management system for research organizations',
    revenue: 150,
    risk: 8,
    framework: 'GxP',
    requirements: [
      { type: BuildingType.RegulatoryRelations, count: 1 },
      { type: BuildingType.InternalAudit, count: 1 },
      { type: BuildingType.ContinuousMonitoring, count: 1 },
      { type: BuildingType.DataProtectionOfficer, count: 1 }
    ],
  },
  // New: Mental Health Platform
  {
    id: 'health_mental',
    category: 'healthtech',
    name: 'Mental Health Platform',
    description: 'Digital platform for mental health support and therapy sessions',
    revenue: 110,
    risk: 7,
    framework: 'HIPAA',
    requirements: [
      { type: BuildingType.LegalPrivacy, count: 2 },
      { type: BuildingType.SecurityTraining, count: 1 },
      { type: BuildingType.CloudWAF, count: 1 }
    ],
  },
  // Enhanced: Health Analytics
  {
    id: 'health_analytics',
    category: 'healthtech',
    name: 'Advanced Health Analytics',
    description: 'AI-powered analytics for population health and predictive medicine',
    revenue: 140,
    risk: 8,
    framework: 'HIPAA',
    requirements: [
      { type: BuildingType.GDPROffice, count: 1 },
      { type: BuildingType.DataProtectionOfficer, count: 1 },
      { type: BuildingType.ThreatIntel, count: 1 },
      { type: BuildingType.APIGateway, count: 1 }
    ],
  },
  // New: Remote Patient Monitoring
  {
    id: 'health_remote',
    category: 'healthtech',
    name: 'Remote Patient Monitoring',
    description: 'Real-time monitoring of patients with chronic conditions',
    revenue: 130,
    risk: 8,
    framework: 'HIPAA',
    requirements: [
      { type: BuildingType.Monitoring, count: 2 },
      { type: BuildingType.CloudWAF, count: 1 },
      { type: BuildingType.DataProtectionOfficer, count: 1 }
    ],
  },
  // New: Medical Imaging AI
  {
    id: 'health_imaging',
    category: 'healthtech',
    name: 'Medical Imaging AI',
    description: 'AI-powered diagnostic imaging with deep learning analysis',
    revenue: 200,
    risk: 9,
    framework: 'FDA',
    requirements: [
      { type: BuildingType.RegulatoryRelations, count: 2 },
      { type: BuildingType.Monitoring, count: 1 },
      { type: BuildingType.InternalAudit, count: 1 },
      { type: BuildingType.ThreatIntel, count: 1 }
    ],
  }
];

export const INSURTECH_PRODUCTS: IndustryProduct[] = [
  {
    id: 'insur_auto',
    category: 'insurtech',
    name: 'Auto Insurance Platform',
    description: 'Digital auto insurance with telematics and usage-based pricing',
    revenue: 160,
    risk: 7,
    framework: 'NYDFS',
    requirements: [
      { type: BuildingType.ClaimsProcessing, count: 1 },
      { type: BuildingType.FraudInvestigation, count: 1 },
      { type: BuildingType.Monitoring, count: 1 }
    ],
  },
  {
    id: 'insur_health',
    category: 'insurtech',
    name: 'Digital Health Insurance',
    description: 'Health insurance platform with wellness incentives',
    revenue: 180,
    risk: 8,
    framework: 'HIPAA',
    requirements: [
      { type: BuildingType.LegalPrivacy, count: 2 },
      { type: BuildingType.RiskAssessment, count: 1 },
      { type: BuildingType.DataProtectionOfficer, count: 1 }
    ],
  },
  {
    id: 'insur_claims',
    category: 'insurtech',
    name: 'AI Claims Processing',
    description: 'Automated claims assessment with computer vision',
    revenue: 200,
    risk: 8,
    framework: 'SOC 2',
    requirements: [
      { type: BuildingType.ClaimsProcessing, count: 2 },
      { type: BuildingType.FraudInvestigation, count: 1 },
      { type: BuildingType.ThreatIntel, count: 1 }
    ],
  },
  {
    id: 'insur_underwriting',
    category: 'insurtech',
    name: 'Smart Underwriting',
    description: 'AI-driven risk assessment and pricing',
    revenue: 190,
    risk: 9,
    framework: 'ISO 27001',
    requirements: [
      { type: BuildingType.RiskAssessment, count: 2 },
      { type: BuildingType.InternalAudit, count: 1 },
      { type: BuildingType.RegulatoryRelations, count: 1 }
    ],
  }
];

export const EDTECH_PRODUCTS: IndustryProduct[] = [
  {
    id: 'ed_lms',
    category: 'edtech',
    name: 'Learning Management System',
    description: 'Comprehensive platform for online education delivery',
    revenue: 120,
    risk: 5,
    framework: 'FERPA',
    requirements: [
      { type: BuildingType.StudentRecords, count: 1 },
      { type: BuildingType.ContentModeration, count: 1 },
      { type: BuildingType.CloudWAF, count: 1 }
    ],
  },
  {
    id: 'ed_virtual_class',
    category: 'edtech',
    name: 'Virtual Classroom',
    description: 'Interactive online learning environment with video',
    revenue: 140,
    risk: 6,
    framework: 'COPPA',
    requirements: [
      { type: BuildingType.ContentModeration, count: 1 },
      { type: BuildingType.AccessibilityLab, count: 1 },
      { type: BuildingType.CloudWAF, count: 1 }
    ],
  },
  {
    id: 'ed_assessment',
    category: 'edtech',
    name: 'Assessment Platform',
    description: 'Secure online testing and evaluation system',
    revenue: 130,
    risk: 7,
    framework: 'WCAG',
    requirements: [
      { type: BuildingType.AccessibilityLab, count: 1 },
      { type: BuildingType.StudentRecords, count: 1 },
      { type: BuildingType.SecurityFraud, count: 1 }
    ],
  },
  {
    id: 'ed_career',
    category: 'edtech',
    name: 'Career Readiness Platform',
    description: 'Skills assessment and career pathway development',
    revenue: 150,
    risk: 6,
    framework: 'GDPR',
    requirements: [
      { type: BuildingType.StudentRecords, count: 1 },
      { type: BuildingType.DataProtectionOfficer, count: 1 },
      { type: BuildingType.LegalPrivacy, count: 1 }
    ],
  }
];

export const RETAIL_PRODUCTS: IndustryProduct[] = [
  {
    id: 'retail_marketplace',
    category: 'retail_ecom',
    name: 'Online Marketplace',
    description: 'Multi-vendor e-commerce platform',
    revenue: 180,
    risk: 7,
    framework: 'PCI-DSS',
    requirements: [
      { type: BuildingType.PaymentProcessing, count: 1 },
      { type: BuildingType.SecurityFraud, count: 1 },
      { type: BuildingType.CloudWAF, count: 1 }
    ],
  },
  {
    id: 'retail_pos',
    category: 'retail_ecom',
    name: 'Omnichannel POS',
    description: 'Unified point-of-sale for online and in-store',
    revenue: 160,
    risk: 8,
    framework: 'PCI-DSS',
    requirements: [
      { type: BuildingType.PaymentProcessing, count: 2 },
      { type: BuildingType.InventoryManagement, count: 1 },
      { type: BuildingType.SecurityFraud, count: 1 }
    ],
  },
  {
    id: 'retail_ai',
    category: 'retail_ecom',
    name: 'AI Personalization Engine',
    description: 'Product recommendations and personalization',
    revenue: 200,
    risk: 7,
    framework: 'GDPR',
    requirements: [
      { type: BuildingType.CustomerAnalytics, count: 1 },
      { type: BuildingType.DataProtectionOfficer, count: 1 },
      { type: BuildingType.CloudWAF, count: 1 }
    ],
  },
  {
    id: 'retail_supply',
    category: 'retail_ecom',
    name: 'Supply Chain Management',
    description: 'End-to-end inventory and logistics platform',
    revenue: 190,
    risk: 8,
    framework: 'ISO 27001',
    requirements: [
      { type: BuildingType.InventoryManagement, count: 2 },
      { type: BuildingType.APIGateway, count: 1 },
      { type: BuildingType.Monitoring, count: 1 }
    ],
  }
];

export const SAAS_PRODUCTS: IndustryProduct[] = [
  // Core Enterprise Solutions
  {
    id: 'saas_erp',
    category: 'saas_b2b',
    name: 'Enterprise ERP Suite',
    description: 'Comprehensive enterprise resource planning with industry modules',
    revenue: 150,
    risk: 6,
    framework: 'ISO 27001',
    requirements: [
      { type: BuildingType.ComplianceMgmt, count: 2 },
      { type: BuildingType.Monitoring, count: 1 },
      { type: BuildingType.InternalAudit, count: 1 }
    ],
  },
  // Industry-Specific Solutions
  {
    id: 'saas_legaltech',
    category: 'saas_b2b',
    name: 'LegalTech Platform',
    description: 'Practice management for law firms with document automation',
    revenue: 180,
    risk: 7,
    framework: 'SOC 2',
    requirements: [
      { type: BuildingType.LegalPrivacy, count: 2 },
      { type: BuildingType.APIGateway, count: 1 },
      { type: BuildingType.CloudWAF, count: 1 }
    ],
  },
  {
    id: 'saas_proptech',
    category: 'saas_b2b',
    name: 'PropTech Suite',
    description: 'Real estate management and transaction platform',
    revenue: 160,
    risk: 6,
    framework: 'SOC 2',
    requirements: [
      { type: BuildingType.LegalDisputes, count: 1 },
      { type: BuildingType.Monitoring, count: 1 },
      { type: BuildingType.CloudWAF, count: 1 }
    ],
  },
  // Vertical SaaS
  {
    id: 'saas_healthcare',
    category: 'saas_b2b',
    name: 'Healthcare Vertical SaaS',
    description: 'Specialized solutions for healthcare providers',
    revenue: 190,
    risk: 8,
    framework: 'HIPAA',
    requirements: [
      { type: BuildingType.GDPROffice, count: 1 },
      { type: BuildingType.DataProtectionOfficer, count: 1 },
      { type: BuildingType.SecurityTraining, count: 1 }
    ],
  },
  {
    id: 'saas_edtech',
    category: 'saas_b2b',
    name: 'EdTech Platform',
    description: 'Learning management system for educational institutions',
    revenue: 140,
    risk: 5,
    framework: 'FERPA',
    requirements: [
      { type: BuildingType.LegalPrivacy, count: 1 },
      { type: BuildingType.SecurityTraining, count: 1 },
      { type: BuildingType.CloudWAF, count: 1 }
    ],
  },
  // Developer Tools
  {
    id: 'saas_devops',
    category: 'saas_b2b',
    name: 'DevOps Platform',
    description: 'End-to-end development and deployment automation',
    revenue: 170,
    risk: 7,
    framework: 'SOC 2',
    requirements: [
      { type: BuildingType.APIGateway, count: 2 },
      { type: BuildingType.ThreatIntel, count: 1 },
      { type: BuildingType.PenTestLab, count: 1 }
    ],
  },
  {
    id: 'saas_api',
    category: 'saas_b2b',
    name: 'API Management',
    description: 'Enterprise-grade API gateway and management',
    revenue: 160,
    risk: 7,
    framework: 'SOC 2',
    requirements: [
      { type: BuildingType.APIGateway, count: 2 },
      { type: BuildingType.CloudWAF, count: 1 },
      { type: BuildingType.ThreatIntel, count: 1 }
    ],
  },
  // Security and Compliance
  {
    id: 'saas_secops',
    category: 'saas_b2b',
    name: 'SecOps Platform',
    description: 'Security operations and threat detection',
    revenue: 200,
    risk: 8,
    framework: 'SOC 2',
    requirements: [
      { type: BuildingType.SOC, count: 2 },
      { type: BuildingType.ThreatIntel, count: 2 },
      { type: BuildingType.PenTestLab, count: 1 }
    ],
  },
  // Government Cloud
  {
    id: 'saas_govcloud',
    category: 'saas_b2b',
    name: 'GovCloud Services',
    description: 'FedRAMP High authorized cloud infrastructure',
    revenue: 300,
    risk: 10,
    framework: 'FedRAMP',
    requirements: [
      { type: BuildingType.RegulatoryRelations, count: 2 },
      { type: BuildingType.SOC, count: 2 },
      { type: BuildingType.ContinuousMonitoring, count: 1 },
      { type: BuildingType.ZeroTrust, count: 1 },
      { type: BuildingType.Vault, count: 1 }
    ],
  }
];

// ============================================
// INDUSTRY MISSIONS
// ============================================

export const FINTECH_MISSIONS: IndustryMission[] = [
  {
    id: 'fin_mission_kyc',
    category: 'fintech',
    title: 'KYC Fundamentals',
    description: 'Implement basic Know Your Customer verification',
    objectives: [
      'Build AML Unit',
      'Verify 100 customer identities',
      'Maintain 0 false positives'
    ],
    rewards: { budget: 500, compliance: 10 },
    difficulty: 'Easy',
    estimatedTime: '10 min'
  },
  {
    id: 'fin_mission_aml',
    category: 'fintech',
    title: 'AML Alert Triage',
    description: 'Clear the suspicious activity alert queue',
    objectives: [
      'Build 2 AML Units',
      'Process 50 alerts',
      'File 5 SARs correctly'
    ],
    rewards: { budget: 800, compliance: 15, unlocks: ['fin_lending'] },
    difficulty: 'Medium',
    estimatedTime: '20 min',
    prerequisites: ['fin_mission_kyc']
  },
  {
    id: 'fin_mission_pci',
    category: 'fintech',
    title: 'PCI Self-Assessment',
    description: 'Complete PCI-DSS self-assessment questionnaire',
    objectives: [
      'Build Firewall + SIEM',
      'Complete SAQ-D',
      'Pass vulnerability scan'
    ],
    rewards: { budget: 1000, compliance: 20, unlocks: ['fin_cards'] },
    difficulty: 'Hard',
    estimatedTime: '30 min',
    prerequisites: ['fin_mission_aml']
  }
];

export const HEALTHTECH_MISSIONS: IndustryMission[] = [
  {
    id: 'health_mission_hipaa',
    category: 'healthtech',
    title: 'HIPAA Training',
    description: 'Complete workforce HIPAA training program',
    objectives: [
      'Build Privacy Office',
      'Train 100% of workforce',
      'Pass knowledge assessment'
    ],
    rewards: { budget: 400, compliance: 10 },
    difficulty: 'Easy',
    estimatedTime: '10 min'
  },
  {
    id: 'health_mission_risk',
    category: 'healthtech',
    title: 'Risk Assessment',
    description: 'Conduct annual HIPAA risk analysis',
    objectives: [
      'Build Audit Lab',
      'Identify 10 risks',
      'Create mitigation plan'
    ],
    rewards: { budget: 600, compliance: 15, unlocks: ['health_analytics'] },
    difficulty: 'Medium',
    estimatedTime: '20 min',
    prerequisites: ['health_mission_hipaa']
  },
  {
    id: 'health_mission_breach',
    category: 'healthtech',
    title: 'Breach Response Drill',
    description: 'Simulate and respond to a PHI breach',
    objectives: [
      'Build Incident Response',
      'Contain breach in 24 hours',
      'Complete notification process'
    ],
    rewards: { budget: 1200, compliance: 25, unlocks: ['health_imaging'] },
    difficulty: 'Hard',
    estimatedTime: '30 min',
    prerequisites: ['health_mission_risk']
  }
];

export const INSURTECH_MISSIONS: IndustryMission[] = [
  {
    id: 'insur_mission_compliance',
    category: 'insurtech',
    title: 'Regulatory Compliance Setup',
    description: 'Establish basic insurance regulatory compliance',
    objectives: [
      'Build Compliance Management',
      'Document policies',
      'Pass initial audit'
    ],
    rewards: { budget: 600, compliance: 12 },
    difficulty: 'Easy',
    estimatedTime: '15 min'
  },
  {
    id: 'insur_mission_fraud',
    category: 'insurtech',
    title: 'Fraud Detection System',
    description: 'Implement anti-fraud mechanisms',
    objectives: [
      'Build 2 Fraud Detection Units',
      'Detect 50 fraudulent claims',
      'Reduce false positives'
    ],
    rewards: { budget: 900, compliance: 15 },
    difficulty: 'Medium',
    estimatedTime: '25 min'
  }
];

export const EDTECH_MISSIONS: IndustryMission[] = [
  {
    id: 'ed_mission_privacy',
    category: 'edtech',
    title: 'Student Data Privacy',
    description: 'Implement FERPA-compliant data protection',
    objectives: [
      'Build Privacy Office',
      'Encrypt student records',
      'Pass privacy audit'
    ],
    rewards: { budget: 500, compliance: 10 },
    difficulty: 'Easy',
    estimatedTime: '15 min'
  },
  {
    id: 'ed_mission_accessibility',
    category: 'edtech',
    title: 'Accessibility Compliance',
    description: 'Ensure WCAG 2.1 AA compliance',
    objectives: [
      'Audit platform accessibility',
      'Fix 30 accessibility issues',
      'Pass WCAG assessment'
    ],
    rewards: { budget: 700, compliance: 12 },
    difficulty: 'Medium',
    estimatedTime: '20 min'
  }
];

export const RETAIL_MISSIONS: IndustryMission[] = [
  {
    id: 'retail_mission_pci',
    category: 'retail_ecom',
    title: 'PCI-DSS Compliance',
    description: 'Achieve PCI Data Security Standard compliance',
    objectives: [
      'Build Firewall + SIEM',
      'Implement 12 core requirements',
      'Pass PCI scan'
    ],
    rewards: { budget: 800, compliance: 15 },
    difficulty: 'Medium',
    estimatedTime: '25 min'
  },
  {
    id: 'retail_mission_fraud',
    category: 'retail_ecom',
    title: 'Payment Fraud Prevention',
    description: 'Reduce fraudulent transactions',
    objectives: [
      'Build 2 Fraud Detection Units',
      'Reduce fraud by 50%',
      'Maintain customer trust'
    ],
    rewards: { budget: 1000, compliance: 18 },
    difficulty: 'Hard',
    estimatedTime: '30 min'
  }
];

export const SAAS_MISSIONS: IndustryMission[] = [
  {
    id: 'saas_mission_controls',
    category: 'saas_b2b',
    title: 'Control Implementation',
    description: 'Deploy foundational security controls',
    objectives: [
      'Build Compliance Management',
      'Implement 25 controls',
      'Document evidence'
    ],
    rewards: { budget: 500, compliance: 10 },
    difficulty: 'Easy',
    estimatedTime: '10 min'
  },
  {
    id: 'saas_mission_vendor',
    category: 'saas_b2b',
    title: 'Vendor Assessment',
    description: 'Evaluate third-party vendor security',
    objectives: [
      'Build Vendor Management',
      'Assess 5 vendors',
      'Create risk register'
    ],
    rewards: { budget: 700, compliance: 15, unlocks: ['saas_erp'] },
    difficulty: 'Medium',
    estimatedTime: '20 min',
    prerequisites: ['saas_mission_controls']
  },
  {
    id: 'saas_mission_pentest',
    category: 'saas_b2b',
    title: 'Penetration Test',
    description: 'Complete annual penetration testing',
    objectives: [
      'Build PenTest Lab',
      'Find 10 vulnerabilities',
      'Remediate critical findings'
    ],
    rewards: { budget: 1000, compliance: 20, unlocks: ['saas_secops'] },
    difficulty: 'Hard',
    estimatedTime: '30 min',
    prerequisites: ['saas_mission_vendor']
  }
];

// ============================================
// REGULATORY FRAMEWORKS
// ============================================

export const REGULATORY_FRAMEWORKS: RegulatoryFramework[] = [
  // Fintech
  {
    id: 'pci_dss',
    name: 'Payment Card Industry Data Security Standard',
    shortName: 'PCI-DSS',
    category: 'fintech',
    description: 'Security standard for organizations handling credit card data',
    requirements: ['Firewall', 'Encryption', 'Access Control', 'Monitoring', 'Testing'],
    penalties: 'Fines $5,000-$100,000/month, loss of card processing',
    icon: '💳'
  },
  {
    id: 'aml_kyc',
    name: 'Anti-Money Laundering / Know Your Customer',
    shortName: 'AML/KYC',
    category: 'fintech',
    description: 'Regulations to prevent financial crimes and verify customer identity',
    requirements: ['Customer ID', 'Transaction Monitoring', 'SAR Filing', 'Record Keeping'],
    penalties: 'Criminal prosecution, massive fines, license revocation',
    icon: '🔍'
  },
  // HealthTech
  {
    id: 'hipaa',
    name: 'Health Insurance Portability and Accountability Act',
    shortName: 'HIPAA',
    category: 'healthtech',
    description: 'Federal law protecting patient health information privacy',
    requirements: ['Privacy Rule', 'Security Rule', 'Breach Notification', 'BAAs'],
    penalties: 'Fines $100-$50,000 per violation, up to $1.5M annually',
    icon: '🏥'
  },
  {
    id: 'fda_21cfr11',
    name: 'FDA 21 CFR Part 11',
    shortName: 'FDA Part 11',
    category: 'healthtech',
    description: 'FDA regulations for electronic records and signatures',
    requirements: ['Audit Trails', 'Access Controls', 'Validation', 'Electronic Signatures'],
    penalties: 'Warning letters, product seizure, injunctions',
    icon: '💊'
  },
  // B2B SaaS
  {
    id: 'soc2',
    name: 'Service Organization Control 2',
    shortName: 'SOC 2',
    category: 'saas_b2b',
    description: 'Trust service criteria for service organizations',
    requirements: ['Security', 'Availability', 'Processing Integrity', 'Confidentiality', 'Privacy'],
    penalties: 'Loss of enterprise customers, failed audits',
    icon: '🔒'
  },
  {
    id: 'gdpr',
    name: 'General Data Protection Regulation',
    shortName: 'GDPR',
    category: 'saas_b2b',
    description: 'EU regulation on data protection and privacy',
    requirements: ['Consent', 'Data Subject Rights', 'DPO', 'Privacy by Design', 'Breach Notification'],
    penalties: 'Up to 4% of global annual revenue or €20M',
    icon: '🇪🇺'
  },
  {
    id: 'fedramp',
    name: 'Federal Risk and Authorization Management Program',
    shortName: 'FedRAMP',
    category: 'saas_b2b',
    description: 'US government cloud security authorization program',
    requirements: ['NIST 800-53 Controls', '3PAO Assessment', 'Continuous Monitoring', 'POA&M'],
    penalties: 'Loss of government contracts, debarment',
    icon: '🏛️'
  }
];

// ============================================
// HELPER FUNCTIONS
// ============================================

export const getCategoryById = (id: IndustryCategory): CategoryConfig | undefined => {
  return INDUSTRY_CATEGORIES.find(cat => cat.id === id);
};

export const getScenariosByCategory = (category: IndustryCategory): IndustryScenario[] => {
  switch (category) {
    case 'fintech': return FINTECH_SCENARIOS;
    case 'healthtech': return HEALTHTECH_SCENARIOS;
    case 'saas_b2b': return SAAS_SCENARIOS;
    case 'insurtech': return [];
    case 'edtech': return [];
    case 'retail_ecom': return [];
    case 'sandbox': return [...FINTECH_SCENARIOS, ...HEALTHTECH_SCENARIOS, ...SAAS_SCENARIOS];
    default: return [];
  }
};

export const getProductsByCategory = (category: IndustryCategory): IndustryProduct[] => {
  switch (category) {
    case 'fintech': return FINTECH_PRODUCTS;
    case 'healthtech': return HEALTHTECH_PRODUCTS;
    case 'saas_b2b': return SAAS_PRODUCTS;
    case 'insurtech': return INSURTECH_PRODUCTS;
    case 'edtech': return EDTECH_PRODUCTS;
    case 'retail_ecom': return RETAIL_PRODUCTS;
    case 'sandbox': return [...FINTECH_PRODUCTS, ...HEALTHTECH_PRODUCTS, ...SAAS_PRODUCTS, ...INSURTECH_PRODUCTS, ...EDTECH_PRODUCTS, ...RETAIL_PRODUCTS];
    default: return [];
  }
};

export const getMissionsByCategory = (category: IndustryCategory): IndustryMission[] => {
  switch (category) {
    case 'fintech': return FINTECH_MISSIONS;
    case 'healthtech': return HEALTHTECH_MISSIONS;
    case 'saas_b2b': return SAAS_MISSIONS;
    case 'insurtech': return INSURTECH_MISSIONS;
    case 'edtech': return EDTECH_MISSIONS;
    case 'retail_ecom': return RETAIL_MISSIONS;
    case 'sandbox': return [...FINTECH_MISSIONS, ...HEALTHTECH_MISSIONS, ...SAAS_MISSIONS, ...INSURTECH_MISSIONS, ...EDTECH_MISSIONS, ...RETAIL_MISSIONS];
    default: return [];
  }
};

export const getFrameworksByCategory = (category: IndustryCategory): RegulatoryFramework[] => {
  if (category === 'sandbox') return REGULATORY_FRAMEWORKS;
  return REGULATORY_FRAMEWORKS.filter(f => f.category === category);
};
